import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Grid, TextField, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Alert from '@material-ui/lab/Alert';
import getApiRoute from '../../../routes/apiRoutes';
import SecureFetchContext from '../../../context/SecureFetchContext';
import AppContext from '../../../context/AppContext';
import { getCsvMappingFields, isBasicShipperVersion } from '../../../config/shipperPackageFormConfig';

const SelectCsvFields = ({ csv, csvMap, setCsvMap, shipper, shipperShortcut, ...rest }) => {
    const { t } = useTranslation();
    const secureFetch = useContext(SecureFetchContext);
    const { appValues } = useContext(AppContext);
    const [stateTo, setStateTo] = useState(null);
    const [statesTo, setStatesTo] = useState([]);

    const shortcut = shipperShortcut ?? (rest.type === 'gls-parcelshop' ? 'GLS_PARCELSHOP' : null);
    const isBasicVersion = useMemo(
        () => isBasicShipperVersion(appValues, { id: shipper, shortcut }),
        [appValues, shipper, shortcut]
    );
    const { base: baseFields, additional: additionalFields, extra: extraFields } = useMemo(
        () => getCsvMappingFields(shortcut, { isBasicVersion }),
        [isBasicVersion, shortcut]
    );
    const requiredSet = useMemo(
        () => new Set([...baseFields, ...additionalFields].filter((f) => f.required).map((f) => f.name)),
        [baseFields, additionalFields]
    );

    useEffect(() => {
        secureFetch(getApiRoute('price_list_states', ['to', shipper])).then((json) => {
            if (csvMap['receiver_state_code_fix'] !== undefined) {
                const tmpState = json.find((s) => s.code === csvMap['receiver_state_code_fix']) || null;
                setStateTo(tmpState);
            }
            setStatesTo(json);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secureFetch, shipper]);

    const onChange = (field, val) => {
        if (val !== -1) {
            setCsvMap({...csvMap, [field]: val});
        } else {
            delete csvMap[field];
            setCsvMap({...csvMap});
        }
    };

    const labelKey = (name) => (name === 'reference_number' ? name : name.replace('receiver_', ''));

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
                <Grid container spacing={1}>
                    {baseFields.map((field, index) => (
                        <React.Fragment key={field.name}>
                            <Grid item xs={12} md={field.name === 'receiver_state_code' ? 6 : null}>
                                <Autocomplete
                                    options={csv.meta.fields}
                                    value={csvMap[field.name] !== undefined ? csv.meta.fields[csvMap[field.name]] : null}
                                    onChange={(event, val) => onChange(field.name, csv.meta.fields.indexOf(val))}
                                    getOptionLabel={(option) => option}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('label.' + labelKey(field.name))}
                                            required={requiredSet.has(field.name)}
                                        />
                                    )}
                                />
                            </Grid>
                            {field.name === 'receiver_state_code' && (
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        options={statesTo}
                                        value={stateTo}
                                        onChange={(event, val) => {
                                            setStateTo(val);
                                            onChange('receiver_state_code_fix', val ? val.code : -1);
                                        }}
                                        getOptionLabel={(option) => option.code}
                                        getOptionSelected={(option, value) => option.code === value.code}
                                        renderInput={(params) => (
                                            <TextField {...params} label={t('label.state_code')} />
                                        )}
                                    />
                                </Grid>
                            )}
                        </React.Fragment>
                    ))}
                </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
                <Grid container spacing={1}>
                    {additionalFields.map((field) => (
                        <Grid key={field.name} item xs={12}>
                            <Autocomplete
                                options={csv.meta.fields}
                                value={csvMap[field.name] !== undefined ? csv.meta.fields[csvMap[field.name]] : null}
                                onChange={(event, val) => onChange(field.name, csv.meta.fields.indexOf(val))}
                                getOptionLabel={(option) => option}
                                renderInput={(params) => (
                                    <TextField {...params} label={t('label.' + field.name)} />
                                )}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Grid>
            {extraFields.length > 0 && (
                <Grid item xs={12} md={4}>
                    <Box mb={1}>
                        <Alert severity="info">
                            <Typography variant="body2">
                                {t('messages.csv_parcelshop_hint')}
                            </Typography>
                        </Alert>
                    </Box>
                    <Grid container spacing={1}>
                        {extraFields.map((field) => (
                            <Grid key={field.name} item xs={12}>
                                <Autocomplete
                                    options={csv.meta.fields}
                                    value={csvMap[field.name] !== undefined ? csv.meta.fields[csvMap[field.name]] : null}
                                    onChange={(event, val) => onChange(field.name, csv.meta.fields.indexOf(val))}
                                    getOptionLabel={(option) => option}
                                    renderInput={(params) => (
                                        <TextField {...params} label={t('label.' + (field.labelKey || field.name))} />
                                    )}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};

export default SelectCsvFields;
